import { SecurityWithRank } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/securityRankingTypes.ts';
import { makeSecurityRankId } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/securityRankingUtils.ts';
import { RankContainerStyles } from '@dealstack/frontend/shared/components/RankContainer/RankContainer.styles.tsx';
import { RankSecurityDropdown } from '@dealstack/frontend/shared/components/RankContainer/RankSecurityDropdown.tsx';
import { SecurityPill } from '@dealstack/frontend/shared/components/SecurityPill/SecurityPill.tsx';
import { getSecurityFeatureTypeColors } from '@dealstack/frontend/shared/components/SecurityRankCard/securityFeatureTypeColors.ts';
import { SortTag } from '@dealstack/frontend/shared/components/SortTag/SortTag.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useSecurityFeatureTypeTranslation } from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController.ts';
import { SecurityFeatureType } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Button, Text, useMantineTheme } from '@mantine/core';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { MantineMenuItemProps } from '@dealstack/frontend/shared/components/MantineMenu/MantineMenu.types';

interface Props {
	name: string;
	hasSecurities: boolean;
	displaySortOrder: number;
	isTracking: boolean;
	isResidue: boolean;
	zIndex: number;
	onRemove: () => void;
	onMoveUp: () => void;
	onMoveDown: () => void;
	onUpdateName: (newName: string) => void;
	validUnrankedSecurities: SecurityWithRank[];
	onUnrankedDropdownItemClick: (security: SecurityWithRank) => void;
	children?: ReactNode;
	unsupportedMessage?: string;
	isLastRank?: boolean;
}

export const RankContainer = ({
	name,
	hasSecurities,
	displaySortOrder,
	isTracking,
	isResidue,
	zIndex,
	children,
	onMoveUp,
	onMoveDown,
	onRemove,
	onUpdateName,
	validUnrankedSecurities,
	onUnrankedDropdownItemClick,
	unsupportedMessage,
	isLastRank,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const tSecurityFeatureTypeTranslation = useSecurityFeatureTypeTranslation();
	const mantineTheme = useMantineTheme();
	let rankName = name;

	if (isTracking) {
		rankName = tSecurityFeatureTypeTranslation(SecurityFeatureType.TRACKING);
	} else if (isResidue) {
		rankName = tSecurityFeatureTypeTranslation(SecurityFeatureType.RESIDUE);
	}

	const inputRef = useRef<HTMLInputElement>(null);
	const [isEditing, setIsEditing] = useState(false);
	const [editedName, setEditedName] = useState(rankName);

	const editActionsDisabled = isTracking || isResidue;

	useEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isEditing]);

	useEffect(() => {
		if (!isEditing) {
			setEditedName(rankName);
		}
	}, [rankName, isEditing]);

	const mappedSecurities = useMemo<MantineMenuItemProps[]>(() => {
		return validUnrankedSecurities?.map((security) => {
			const key = makeSecurityRankId(security);
			const featureType = tSecurityFeatureTypeTranslation(security.securityFeatureType);

			return {
				key,
				label: `${security.securityName} (${featureType})`,
				onClick: () => onUnrankedDropdownItemClick(security),
			};
		});
	}, [validUnrankedSecurities, onUnrankedDropdownItemClick]);

	const rankTypePill = useMemo(() => {
		if (isTracking || isResidue) {
			const { borderColor, color } = getSecurityFeatureTypeColors(
				mantineTheme,
				isTracking ? SecurityFeatureType.TRACKING : SecurityFeatureType.RESIDUE
			);
			const rankTypeLabel = tSecurityFeatureTypeTranslation(
				isTracking ? SecurityFeatureType.TRACKING : SecurityFeatureType.RESIDUE
			);

			return <SecurityPill label={rankTypeLabel} borderColor={borderColor} color={color} />;
		}

		return null;
	}, [isTracking, isResidue, mantineTheme, tSecurityFeatureTypeTranslation]);

	const handleNameClick = () => {
		if (editActionsDisabled) {
			return;
		}

		setEditedName(rankName);
		setIsEditing(true);
	};

	const handleBlur = () => {
		if (editedName.trim() && editedName !== name) {
			onUpdateName(editedName.trim());
		} else {
			setEditedName(name);
		}

		setIsEditing(false);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			inputRef.current?.blur();
		} else if (e.key === 'Escape') {
			setEditedName(name);
			setIsEditing(false);
		}
	};

	return (
		<RankContainerStyles data-testid={TEST_ELEMENT_IDS.CAP_RANK_CONTAINER}>
			<div className="rank-header">
				<div className={`sort-actions ${editActionsDisabled ? 'disabled' : ''}`}>
					<button
						className="caret"
						onClick={onMoveUp}
						data-testid={TEST_ELEMENT_IDS.CAP_RANK_MOVE_UP_BUTTON}
					>
						<Icon variant="caret-up" size="lg" />
					</button>

					<button
						className="caret"
						onClick={onMoveDown}
						data-testid={TEST_ELEMENT_IDS.CAP_RANK_MOVE_DOWN_BUTTON}
					>
						<Icon variant="caret-down" size="lg" />
					</button>
				</div>

				<div className="rank-info">
					<SortTag label={displaySortOrder} />

					<div className="rank-name">
						{isEditing ? (
							<input
								ref={inputRef}
								type="text"
								value={editedName}
								onChange={(e) => setEditedName(e.target.value)}
								onBlur={handleBlur}
								onKeyDown={handleKeyDown}
								className="name-input"
								data-testid={TEST_ELEMENT_IDS.CAP_RANK_NAME_INPUT}
							/>
						) : (
							<Text
								c="dark.9"
								className="display-name"
								onClick={handleNameClick}
								data-testid={TEST_ELEMENT_IDS.CAP_RANK_NAME_TEXT}
							>
								{rankName}
							</Text>
						)}
					</div>
				</div>

				{rankTypePill && <div className="rank-type-pill">{rankTypePill}</div>}

				<div className="rank-dropdown">
					<RankSecurityDropdown
						securities={mappedSecurities}
						disabled={!validUnrankedSecurities?.length}
					/>
				</div>
			</div>

			<div className="rank-content" style={{ zIndex }}>
				{hasSecurities ? (
					<div className="securities-grid">{children}</div>
				) : (
					<div className="empty">
						{isLastRank && (
							<Text mb="0.5rem" fz="lg" c="dark.8">
								{tCapManage('rank.emptyState.only.title')}
							</Text>
						)}

						<Text mb="0.5rem" c="gray.9">
							{isLastRank
								? tCapManage('rank.emptyState.only.description')
								: tCapManage('rank.emptyState.description')}
						</Text>

						<div className="remove-container">
							<Button
								onClick={onRemove}
								variant="subtle"
								color="red"
								mb="0.5rem"
								data-testid={TEST_ELEMENT_IDS.CAP_RANK_REMOVE_BUTTON}
								className="remove-button"
								disabled={isLastRank}
							>
								{tCapManage('rank.empty.remove')}
							</Button>
						</div>

						<div className="securities-grid">{children}</div>
					</div>
				)}

				{unsupportedMessage && (
					<div className="unsupported-overlay">
						<Text c="red.6" fw={500} className="unsupported-message">
							{unsupportedMessage}
						</Text>
					</div>
				)}
			</div>
		</RankContainerStyles>
	);
};
